import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { FilterOperator } from "@/lib/filters";
import type { EntityType } from "@/types/entity";

export interface FieldDefinition {
  id: string;
  display_name: string;
  record_type: string | null;
  data_type: string;
  valid_operators: FilterOperator[];
  has_enum_values: boolean;
  enum_values?: string[] | null;
  is_custom: boolean;
}

export interface MetricDefinition {
  key: string;
  displayName: string;
  dataType: string;
  description?: string;
}

export type FieldDefinitionResponse =
  | FieldDefinition[]
  | { field_definitions: FieldDefinition[] };

/**
 * Fetches unified field definitions for the requested entity.
 * The server guarantees a stable contract – see backend serializer.
 *
 * Response is cached for 10 min because this metadata rarely changes and is
 * expensive to build on the server.
 */
export function useFieldDefinitions(entity: EntityType) {
  return useQuery<FieldDefinition[], Error>({
    queryKey: ["field-definitions", entity],
    queryFn: async () => {
      const response = await api.get<FieldDefinitionResponse>(
        `datahub/${entity}/field-definitions/`,
      );
      if (Array.isArray(response)) {
        return response;
      }

      return response.field_definitions || [];
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}
